import { useEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import { useTranslation } from 'react-i18next';
import { AuthGuard, useSupabaseSession } from '@ubus/shared';

import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import AccountSecurity from './pages/AccountSecurity';
import Dashboard from './pages/Dashboard';
import TripDetail from './pages/TripDetail';
import LiveMap from './pages/LiveMap';
import TripBooking from './pages/TripBooking';
import Requests from './pages/Requests';
import RequestDetail from './pages/RequestDetail';
import TripHistory from './pages/TripHistory';
import TripFeedback from './pages/TripFeedback';
import TripSharePage from './pages/TripSharePage';
import ClientLayout from './components/ClientLayout';

import './App.css';

function AuthLoadingFallback() {
  const { t } = useTranslation();
  return <Spin size="large" tip={t('common.verifyingAuth')} fullscreen />;
}

function AppProviders({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh-CN';

  useEffect(() => {
    dayjs.locale(isZh ? 'zh-cn' : 'en');
  }, [isZh]);

  return (
    <ConfigProvider
      locale={isZh ? zhCN : enUS}
      theme={{
        token: {
          colorPrimary: '#ea580c',
          colorInfo: '#0369a1',
          colorSuccess: '#059669',
          colorWarning: '#d97706',
          colorError: '#dc2626',
          colorBgLayout: '#f1f5f9',
          colorBorder: '#e2e8f0',
          colorText: '#0f172a',
          colorTextSecondary: '#475569',
          borderRadius: 8,
          fontFamily: "'Noto Sans', 'Figtree', sans-serif",
          fontFamilyCode: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          controlHeight: 36,
        },
        components: {
          Button: { fontWeight: 500 },
          Card: { borderRadiusLG: 12 },
          Menu: { itemBorderRadius: 8 },
        },
      }}
    >
      <AntApp>{children}</AntApp>
    </ConfigProvider>
  );
}

function App() {
  const { session, loading } = useSupabaseSession();

  return (
    <AppProviders>
      <Routes>
        <Route path="/t/:token" element={<TripSharePage />} />
        <Route path="/login" element={session ? <Navigate to="/dashboard" /> : <Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {loading ? (
          <Route path="*" element={<AuthLoadingFallback />} />
        ) : (
          <Route
            path="/"
            element={
              <AuthGuard requiredRole="client" loadingFallback={<AuthLoadingFallback />}>
                <ClientLayout />
              </AuthGuard>
            }
          >
            <Route index element={<Navigate to="/dashboard" />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="account" element={<AccountSecurity />} />
            <Route path="requests" element={<Requests />} />
            <Route path="requests/new" element={<TripBooking />} />
            <Route path="requests/:id" element={<RequestDetail />} />
            <Route path="book" element={<Navigate to="/requests/new" replace />} />
            <Route path="trip/:id" element={<TripDetail />} />
            <Route path="map/:id" element={<LiveMap />} />
            <Route path="history" element={<Navigate to="/trips" replace />} />
            <Route path="trips" element={<TripHistory />} />
            <Route path="feedback" element={<TripFeedback />} />
          </Route>
        )}

        <Route path="*" element={<Navigate to={session ? '/dashboard' : '/login'} />} />
      </Routes>
    </AppProviders>
  );
}

export default App;
